// ** React
import { useState, useEffect } from "react";

// ** Icons
import Check from "mdi-material-ui/Check";
import ChevronDown from "mdi-material-ui/ChevronDown";
import LockOutline from "mdi-material-ui/LockOutline";

// ** Reused neutral helpers (availability, type icon, duration)
import { isChapterAvailable, ChapterAvaliableIn, LessonInfo, LessonLength, AttachmentsInfo } from "src/layouts/components/coursePlayer/PlayList/helper";

/******************************************************************
 * ##: FlixPlaylist
 * Flix-styled course curriculum (chapters -> lessons). New markup, but
 * reuses the neutral player helpers for availability/drip logic, the
 * lesson-type icon, the duration label and the attachments ("Materiais")
 * indicator. Clicking a lesson delegates
 * to the shared activeLessonHandler; the completion toggle delegates to
 * lessonStatusHandler (questionnaire lessons are gated, like the app).
 * Modules render as an accordion the user can expand independently
 * (several open at once); the active chapter starts expanded, and
 * selecting a lesson collapses the rest, leaving only that lesson's
 * module open.
 * @param {Object} props - chapters (details), active ids, status, handlers
 * @returns {JSX.Element} - the `.flix-playlist`
 * History:
 * 22-07-2026: Created
 * 23-07-2026: Module panels as a multi-open accordion; selecting a lesson keeps only that module open
 ******************************************************************/
const FlixPlaylist = ({
  details = [],
  actualChapter,
  actualLesson,
  lessonsStatus = [],
  videos_info,
  courseAddedAt,
  courseEndDate,
  hasTeaserAccess = false,
  showLessonNumbers = false,
  showChapterNumbers = false,
  activeLessonHandler,
  lessonStatusHandler,
}) => {
  // Fill-to-current progress: the spine is lit up to (and including) the active
  // lesson, so it reads as a continuous progress bar independent of per-lesson
  // completion (and never flickers when a lesson is un-marked).
  const activeChapterIdx = details.findIndex((chapter) => chapter.chapter_id === actualChapter);
  const activeLessonIdx =
    activeChapterIdx >= 0 ? details[activeChapterIdx].lessons?.findIndex((lesson) => lesson.lesson_id === actualLesson) ?? -1 : -1;

  // Accordion: the user can manually keep several modules expanded at once (toggleChapter
  // is additive). But selecting a lesson — via a row click or prev/next — focuses that
  // lesson's module: the active chapter stays open and every other panel collapses.
  // Keyed on actualLesson so it also fires when picking another lesson in the same module.
  const [openChapters, setOpenChapters] = useState(() => new Set([actualChapter]));
  useEffect(() => {
    setOpenChapters((prev) => (prev.size === 1 && prev.has(actualChapter) ? prev : new Set([actualChapter])));
  }, [actualChapter, actualLesson]);
  const toggleChapter = (chapterId) =>
    setOpenChapters((prev) => {
      const next = new Set(prev);
      if (next.has(chapterId)) next.delete(chapterId);
      else next.add(chapterId);
      return next;
    });

  return (
    <div className="flix-playlist">
      <h3 className="flix-playlist__heading">Conteúdo do curso</h3>

      {details.map((chapter, chapterIndex) => {
        const available = isChapterAvailable(chapter, courseAddedAt, hasTeaserAccess);
        const isActiveChapter = chapter.chapter_id === actualChapter;
        const isOpen = openChapters.has(chapter.chapter_id);
        const panelId = `flix-chapter-panel-${chapter.chapter_id}`;

        return (
          <section key={chapter.chapter_id} className={`flix-chapter${isActiveChapter ? " active" : ""}${isOpen ? " is-open" : ""}`}>
            <div
              className="flix-chapter__head"
              role="button"
              tabIndex={0}
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={() => toggleChapter(chapter.chapter_id)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  toggleChapter(chapter.chapter_id);
                }
              }}
            >
              <div className="flix-chapter__headmain">
                {showChapterNumbers && <span className="flix-chapter__num">Módulo {chapterIndex + 1}</span>}
                <span className="flix-chapter__title">{chapter.title}</span>
                <ChapterAvaliableIn chapter={chapter} courseAddedAt={courseAddedAt} courseEndDate={courseEndDate} hasTeaserAccess={hasTeaserAccess} />
              </div>
              <ChevronDown className="flix-chapter__chevron" />
            </div>

            <div className="flix-chapter__panel" id={panelId}>
              <ul className="flix-chapter__lessons">
              {chapter.lessons?.map((lesson, lessonIndex) => {
                const isCompleted = lessonsStatus?.find((el) => el.lesson_id === lesson.lesson_id)?.completed === true;
                const isActive = lesson.lesson_id === actualLesson;
                const isQuestionnaire = lesson.lesson_type === "questionnaire";
                const isMedia = lesson.lesson_type === "video" || lesson.lesson_type === "audio";
                const toggleInteractive = available === true && isQuestionnaire !== true;
                const isPassed =
                  available &&
                  activeChapterIdx >= 0 &&
                  (chapterIndex < activeChapterIdx || (chapterIndex === activeChapterIdx && lessonIndex <= activeLessonIdx));

                return (
                  <li
                    key={lesson.lesson_id}
                    className={`flix-lesson${isActive ? " active" : ""}${isCompleted ? " is-completed" : ""}${isPassed ? " is-passed" : ""}${available ? "" : " flix-lesson--locked"}`}
                    onClick={() => activeLessonHandler(chapter.chapter_id, lesson.lesson_id)}
                  >
                    <span
                      className="flix-lesson__check"
                      onClick={(e) => {
                        if (!toggleInteractive) return;
                        e.stopPropagation();
                        lessonStatusHandler(chapter.chapter_id, lesson.lesson_id, !isCompleted);
                      }}
                      role="button"
                      aria-label={isCompleted ? "Desmarcar como concluída" : "Marcar como concluída"}
                    >
                      <span className="flix-lesson__dot">{isCompleted && <Check className="flix-lesson__tick" />}</span>
                    </span>

                    <span className="flix-lesson__body">
                      <LessonInfo lesson={lesson} />
                      <span className="flix-lesson__text">
                        <span className="flix-lesson__title">
                          {showLessonNumbers && <span className="flix-lesson__num">{lessonIndex + 1}.</span>}
                          <span className="flix-lesson__name">{lesson.title}</span>
                          {!available && (
                            <span className="flix-lesson__lock" title="Disponível em breve">
                              <LockOutline />
                            </span>
                          )}
                        </span>
                        <span className="flix-lesson__meta">
                          {isActive && available && (
                            <span className="flix-lesson__now">
                              {isMedia && (
                                <span className="flix-lesson__eq" aria-hidden="true">
                                  <i />
                                  <i />
                                  <i />
                                </span>
                              )}
                              A ver
                            </span>
                          )}
                          {available && (
                            <span className="flix-lesson__dur">
                              <LessonLength videos_info={videos_info} lesson={lesson} />
                            </span>
                          )}
                          {available && lesson.files_content?.length > 0 && <AttachmentsInfo lesson={lesson} />}
                        </span>
                      </span>
                    </span>
                  </li>
                );
              })}
              </ul>
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default FlixPlaylist;
